import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { APIError, FailedAttributes, ValidationAPIError, ValidationErrorMeta } from 'types/error';
import { NotificationSeverity, ToastNotification } from 'types/notifications';

import { beautifyFieldsInQuotes } from './string';

const KEYS = {
  UNKNOWN_ERROR_TEXT: 'Unknown error',
};

export const getValidationDetails = (meta: ValidationErrorMeta) => {
  if (meta?.source?.key) {
    return meta.source.key;
  }

  const hasAttributes = meta.failedAttributes;

  if (!hasAttributes) {
    return undefined;
  }

  const reduce = (details: Record<string, string>, attributes: FailedAttributes) => {
    if (attributes) {
      const { attributeName, message } = attributes;
      const displayMessage = beautifyFieldsInQuotes(message);
      return {
        ...details,
        [attributeName]: displayMessage,
      };
    }

    return details;
  };

  return meta.failedAttributes?.reduce(reduce, {});
};

export const getServerSideValidationDetails = (error: FetchBaseQueryError | SerializedError | undefined, generalErrorField: string) => {
  const errorMeta = error ? ((error as FetchBaseQueryError).data as { meta: ValidationErrorMeta }).meta : undefined;
  const fieldError = errorMeta ? getValidationDetails(errorMeta) : undefined;

  if (error && !fieldError) {
    return { [generalErrorField]: ((error as FetchBaseQueryError).data as APIError).errorMessage };
  }

  return fieldError;
};

export const getErrorDetails = (errorBase: FetchBaseQueryError | undefined, defaultErrorField?: string) => {
  if (!errorBase) {
    return undefined;
  }

  const errorMeta = (errorBase.data as { meta: ValidationErrorMeta }).meta;
  const validationDetails = getValidationDetails(errorMeta);

  if (validationDetails) {
    return validationDetails;
  }

  if (defaultErrorField) {
    return { [defaultErrorField]: (errorBase.data as APIError).errorMessage };
  }

  return (errorBase.data as APIError).errorMessage;
};

export const isFetchBaseQueryErrorWithData = (error: unknown): error is FetchBaseQueryError & { data: APIError } => {
  return typeof error === 'object' && error !== null && 'data' in error;
};

export const handleGenericApiError = (
  error: SerializedError | FetchBaseQueryError | undefined,
  pushToast: (params: Pick<ToastNotification, 'severity' | 'message'>) => void,
): boolean => {
  if (!error) {
    return false;
  }

  if (isFetchBaseQueryErrorWithData(error)) {
    pushToast({
      severity: NotificationSeverity.error,
      message: error.data.errorMessage ?? KEYS.UNKNOWN_ERROR_TEXT,
    });
    return true;
  }

  pushToast({
    severity: NotificationSeverity.error,
    message: (error as any)?.message ?? KEYS.UNKNOWN_ERROR_TEXT,
  });
  return true;
};

const convertToCamelCase = (key: string) => key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

export const flattenKeys = (obj: any, prefix = ''): string[] => {
  if (typeof obj !== 'object' || obj === null) return [];

  const keys: string[] = [];

  for (const [key, value] of Object.entries(obj)) {
    // This is to handle Bridge passed errors, this should be handled on BE
    const camelCaseKey = convertToCamelCase(key);
    let path = '';
    if (Array.isArray(obj)) {
      path = `${prefix}[${camelCaseKey}]`;
    } else if (prefix) {
      path = `${prefix}.${camelCaseKey}`;
    } else {
      path = camelCaseKey;
    }

    if (typeof value === 'object' && value !== null) {
      keys.push(...flattenKeys(value, path));
    } else {
      keys.push(path);
    }
  }

  return keys;
};

export const unflattenErrors = (flat: Record<string, any> | undefined) => {
  if (!flat) {
    return {};
  }

  const result: Record<string, any> = {};

  Object.entries(flat).forEach(([path, value]) => {
    const keys = path.replace(/\[\d+\]/g, '').split('.');
    keys.reduce((acc, key, i) => {
      // This is to handle Bridge passed errors, this should be handled on BE
      const camelCaseKey = convertToCamelCase(key);
      if (i === keys.length - 1) acc[camelCaseKey] = value;
      else acc[camelCaseKey] ??= {};
      return acc[camelCaseKey];
    }, result);
  });

  return result;
};

type FailureMeta = { meta?: { source?: { key?: Record<string, string> } } };

export const getValidationErrorMessage = (error?: ValidationAPIError): string | undefined => {
  const failures = (error?.meta as Record<string, unknown>)?.failures as FailureMeta[] | undefined;

  const keyEntry = Object.entries(failures?.[0]?.meta?.source?.key ?? {})[0];
  const failureKeyMessage = keyEntry ? `${keyEntry[0]} ${keyEntry[1]}` : undefined;
  const genericErrorMessage = error?.meta?.failedAttributes?.[0]?.message ?? error?.errorMessage;

  return failureKeyMessage ? `${genericErrorMessage}: \n${failureKeyMessage}` : genericErrorMessage;
};
